/**
 * English catalogue.
 *
 * Typed as the exact shape of the French one, so adding a key to `fr` without
 * translating it here is a compile error rather than a string that silently
 * falls back and appears in the wrong language.
 */
export const en = {
    common: {
        appName: 'Honoré Bagni',
        save: 'Save',
        cancel: 'Cancel',
        confirm: 'Confirm',
        delete: 'Delete',
        close: 'Close',
        back: 'Back',
        next: 'Next',
        search: 'Search',
        loading: 'Loading…',
        retry: 'Try again',
        copy: 'Copy',
        copied: 'Copied',
        yes: 'Yes',
        no: 'No',
        continue: 'Continue',
        optional: 'optional',
        none: 'None',
    },
    nav: {
        account: 'My account',
        admin: 'Administration',
        skipToContent: 'Skip to content',
        chat: 'Chat',
        projects: 'Projects',
        settings: 'Settings',
        photos: 'Photos',
        backup: 'Backup',
    },
    auth: {
        signIn: 'Sign in',
        signOut: 'Sign out',
        signUp: 'Create an account',
        email: 'Email address',
        password: 'Password',
        newPassword: 'New password',
        currentPassword: 'Current password',
        forgotPassword: 'Forgot your password?',
        resetPassword: 'Reset password',
        signupAccepted: 'If that address can be used, you will receive an email shortly.',
        resetAccepted: 'If an account exists for that address, a reset email is on its way.',
        verifyEmailTitle: 'Verify your email address',
        verifyEmailSent: 'We sent a verification link to {email}.',
        resendVerification: 'Resend the email',
        passwordRequirements: 'At least 12 characters. A passphrase is safer than a complicated word.',
        signUpTitle: 'Create your account',
        signUpSubtitle: 'One account for photos and chat.',
        signInTitle: 'Welcome back',
        signInSubtitle: 'Your password first, then your two-factor code.',
        noAccount: 'No account yet?',
        haveAccount: 'Already have an account?',
        verifying: 'Verifying your address…',
        verifyFailed: 'This link is no longer valid. Ask for a new one from the sign-in page.',
        emailResent: 'On its way. Check your inbox.',
        forgotTitle: 'Forgotten password',
        forgotSubtitle: 'We will send you a link to choose a new one.',
        resetTitle: 'Choose a new password',
        resetSubtitle: 'Every one of your devices will be signed out.',
        resetDone: 'Password changed. You can sign in again.',
        changePassword: 'Change password',
        changePasswordHint: 'Your other devices will be signed out.',
        passwordChanged: 'Password changed.',
    },
    totp: {
        title: 'Two-factor authentication',
        required: 'Two-factor authentication is required on every account.',
        scanQr: 'Scan this QR code with your authenticator app.',
        manualEntry: 'Or enter this key manually:',
        enterCode: 'Enter the 6-digit code',
        code: 'Verification code',
        useRecoveryCode: 'Use a recovery code',
        recoveryCode: 'Recovery code',
        recoveryTitle: 'Your recovery codes',
        recoveryWarning: 'Keep these somewhere safe. They will never be shown again, and each one works only once.',
        recoveryDownload: 'Download codes',
        enrolTitle: 'Turn on two-factor authentication',
        enrolSubtitle: 'The last step before your account works.',
        qrAlt: 'QR code containing your secret key',
        confirmCode: 'Turn on',
        challengeSubtitle: 'Open your authenticator app.',
        useAuthenticator: 'Use the authenticator app',
        deviceLabel: 'Name for this device',
        deviceLabelHint: 'So you can recognise it in the list. "Nathan\'s laptop", for instance.',
        recoverySaved: 'I have saved my codes',
    },
    account: {
        title: 'My account',
        language: 'Language',
        plan: 'Plan',
        usage: 'Usage',
        sessions: 'Signed-in devices',
        thisDevice: 'This device',
        revokeSession: 'Sign out this device',
        signOutEverywhere: 'Sign out everywhere',
        lastSeen: 'Seen {when}',
        deleteAccount: 'Delete my account',
        exportData: 'Export my data',
        signedInSince: 'Signed in since {when}',
        theme: 'Theme',
        themeSystem: 'System',
        themeDark: 'Dark',
        themeLight: 'Light',
        applications: 'Applications',
        noOtherDevices: 'No other device is signed in.',
        unnamedDevice: 'Unnamed device',
        planExpires: 'Expires {when}',
        planNeverExpires: 'No expiry',
        usageOf: '{used} of {limit}',
        localeSaved: 'Language saved.',
    },
    deletion: {
        cardHint: 'Erases your photos, albums, groups and conversations for good. You will have an hour to change your mind.',
        title: 'Delete your account',
        whatGoes: 'Everything in your account will be erased: your photos and videos, your albums, the groups you created, your conversations and their files, and your API key. The members of your groups will lose access to them.',
        grace: 'Nothing is erased straight away. Every device will be signed out, and we will email you a link that keeps your account for {minutes} minutes. After that, the deletion is final.',
        backups: 'Copies may remain in our backups for at most {days} days, until they expire.',
        confirm: 'To confirm, enter your password and a code from your authenticator app.',
        submit: 'Delete my account',
        keepInstead: 'No, keep my account',
        scheduledTitle: 'Your account is about to be deleted',
        scheduledWhen: 'It will be erased at {time}. Every device has been signed out.',
        scheduledKeep: 'To keep it, follow the link in the email we have just sent you before then.',
        scheduledUnknown: 'Your account will be erased within the hour. To keep it, follow the link in the email we have just sent you.',
        keepTitle: 'Keep your account',
        keepIntro: 'Your account is due to be deleted within the hour. This button cancels the deletion.',
        keepAction: 'Keep my account',
        kept: 'Your account has been kept. Sign in again to use it.',
        keptAdvice: 'If you did not ask for it to be deleted, change your password as soon as you are signed in.',
        keepFailed: 'This link no longer works: it has already been used, or the hour is over.',
        wrongPassword: 'That password is incorrect.',
        infoTitle: 'Deleting your Honoré Bagni account',
        infoIntro: 'You can delete your account yourself, from your account page:',
        infoStepSignIn: 'Sign in.',
        infoStepOpen: 'Open “My account”, then “Delete my account”.',
        infoStepConfirm: 'Confirm with your password and a two-factor code.',
        infoCannotSignIn: 'Can no longer sign in, having lost both your password and your two-factor authentication? Write to {address} from your account’s address and we will delete it for you.',
        infoContinue: 'Continue to deletion',
        infoSignIn: 'Sign in to delete my account',
    },
    admin: {
        title: 'Administration',
        searchLabel: 'Search an email address',
        // `@` starts vue-i18n's linked-message syntax, so a bare one at the end of
        // a message fails to compile and takes the whole screen down with it. The
        // `{'...'}` form is how a literal is written.
        searchPlaceholder: "start-of-the-address{'@'}",
        filterStatus: 'Status',
        allStatuses: 'All',
        plan: 'Plan',
        grants: 'Access',
        lock: 'Lock',
        unlock: 'Unlock',
        lastLogin: 'Last sign-in',
        never: 'Never',
        loadMore: 'Load more',
        noResults: 'No account matches.',
        saved: 'Account updated.',
        staleSession: 'Sign in again to change an account.',
    },
    chat: {
        title: 'Chat',
        conversations: 'Conversations',
        newConversation: 'New conversation',
        noConversations: 'No conversations yet.',
        conversationsLabel: 'Your conversations',
        showConversations: 'Show conversations',
        hideConversations: 'Hide conversations',
        renameConversation: 'Rename conversation',
        deleteConversation: 'Delete conversation',
        conversationTitle: 'Conversation title',
        allProjects: 'All',
        filterByProject: 'Filter by project',
        loadMore: 'Load more',
        transcriptLabel: 'Conversation transcript',
        emptyTitle: 'Ask your first question',
        emptySubtitle: 'The reply is written as it arrives, and picks itself up again if the connection drops.',
        you: 'You',
        assistant: 'Assistant',
        composerLabel: 'Your message',
        composerPlaceholder: 'Write your message…',
        composerHint: 'Enter to send, Shift+Enter for a new line.',
        send: 'Send',
        stop: 'Stop',
        resume: 'Resume the reply',
        retry: 'Try again',
        // Downloading a reply as a file. The format names are the extensions rather
        // than translated words: `.docx` is `.docx` in every language, and a reader
        // choosing between them is choosing what to open it in.
        exportLabel: 'Download as',
        exportAction: 'Download',
        exportFormatMd: 'Markdown (.md)',
        exportFormatCsv: 'CSV (.csv)',
        exportFormatDocx: 'Word (.docx)',
        exportFormatXlsx: 'Excel (.xlsx)',
        exportFormatPdf: 'PDF (.pdf)',
        // Asking for a picture instead of a reply. The mode belongs on the composer
        // because a generated image is a turn in the conversation rather than a
        // thing beside it.
        modeLabel: 'What to send',
        modeMessage: 'Message',
        modeImage: 'Image',
        draw: 'Draw',
        imageModelLabel: 'Image model',
        promptPlaceholder: 'Describe the picture…',
        // The one thing about this mode a reader cannot guess: nothing appears until
        // it is finished, and that takes a while.
        imageHint: 'A picture takes up to a minute, and arrives all at once.',
        drawing: 'Drawing the picture…',
        // Deliberately generic. The only description available is the prompt, which
        // is the previous message - and repeating the request back at the reader is
        // worse than saying plainly what this is.
        imageAlt: 'Generated picture',
        // Read aloud by the browser's own voice. NVIDIA has no REST speech endpoint,
        // so nothing is generated and nothing is downloaded - see `speech.ts`.
        readAloud: 'Read aloud',
        stopReading: 'Stop reading',
        reasoning: 'Reasoning',
        showReasoning: 'Show reasoning',
        hideReasoning: 'Hide reasoning',
        thinking: 'Thinking…',
        generating: 'Writing the reply…',
        reconnecting: 'Connection lost, picking the reply back up…',
        cancelled: 'Reply interrupted.',
        model: 'Model',
        reasoningEffort: 'Reasoning effort',
        // Says what is true - we have no dialect to send the setting in - rather than
        // "this model does not reason", which is a claim the catalogue cannot back. A
        // model nobody has written a rule for falls to a conservative fallback and
        // may well reason anyway: `z-ai/glm-5.3-flash` did, for an hour, while this
        // control sat greyed out saying it could not.
        reasoningUnsupported: 'Reasoning effort cannot be set for this model.',
        // Each choosable model carries its own window, because the question the
        // picker is actually being asked is "which of these has room", and answering
        // it only for the model already selected means opening the list, choosing
        // blind, and reading the consequence afterwards. Compact, because this is an
        // `<option>`: the precise figure is in `contextTokens` below.
        modelOption: '{name} · {context} context',
        modelRetiredOption: '{name} (no longer available)',
        modelRetired: 'The provider no longer offers this model. Choose another one to carry on this conversation.',
        accepts: 'Accepts: {modalities}',
        // Still said in full under the control, and not redundant with the option
        // label: at 12rem the closed select clips its own text, so on a phone this is
        // the only place the selected model's window survives.
        contextTokens: '{tokens} tokens of context',
        // What the last turn actually cost against what the model can hold. The
        // second form is not a fallback but the honest answer for any model no
        // family rule matches: a made-up ceiling reads as room the reader has not
        // got.
        //
        // The percentage leads because it is the figure that needs no arithmetic -
        // two six-digit numbers are a subtraction the reader should not have to do
        // to find out they are nearly full.
        contextUsedOf: 'Last turn used {percent} of the window — {used} of {total} tokens',
        contextUsedOfUnknown: 'Last turn used {used} tokens (window unknown)',
        // Said out loud because the alternative is silent: past the window the
        // provider drops the *oldest* turns, so the model appears to forget rather
        // than to fail.
        truncated: 'The model ran out of room and stopped part-way. Ask again in fewer words, or start a new conversation.',
        noAnswer: 'The model spent its whole reply thinking and never answered. Try a shorter question, or another model.',
        usage: '{prompt} tokens sent, {completion} received',
        attach: 'Attach',
        attachRemove: 'Remove {name}',
        noKeyTitle: 'Add your API key',
        noKeySubtitle: 'Chat runs on your own NVIDIA key. Nothing is sent until you have saved one.',
        noKeyAction: 'Open settings',
    },
    modality: {
        text: 'text',
        image: 'image',
        audio: 'audio',
    },
    effort: {
        off: 'Off',
        low: 'Low',
        medium: 'Medium',
        high: 'High',
    },
    access: {
        signInTitle: 'Sign-in required',
        signInBody: 'Taking you to the sign-in page.',
        signInAction: 'Continue to sign in',
        noAccessTitle: 'No access to chat',
        noAccessBody: 'Your account does not have access to this application.',
        noAccessAction: 'Go to my account',
    },
    documents: {
        title: 'Documents',
        hint: 'Every conversation in this project, and in the projects under it, can read these.',
        none: 'No documents yet.',
        add: 'Add a document',
        remove: 'Remove',
        accepts: 'PDF, Word, Excel, text, Markdown and CSV files.',
        tokens: '≈ {tokens} tokens',
        afterSaving: 'Documents can be added once the project has been saved.',
        // A large document is read through retrieval, which happens in a background
        // worker - so there is a real gap between filing one and it being usable, and
        // saying nothing would make a document that is not ready look exactly like one
        // that is.
        indexing: 'Being read…',
        indexed: 'Searchable',
        failed: 'Could not be read',
    },
    projects: {
        title: 'Projects',
        subtitle: 'A project carries a prompt and a default model, which its sub-projects and conversations inherit.',
        none: 'No projects.',
        create: 'New project',
        name: 'Name',
        parent: 'Parent project',
        noParent: 'None (root)',
        systemPrompt: 'Prompt',
        systemPromptHint: 'Prepended to every exchange. You pay for these tokens on every turn, so keep it short.',
        defaultModel: 'Default model',
        inherit: 'Inherit',
        effectivePrompt: 'Effective prompt',
        effectivePromptHint: 'This project’s prompt, preceded by each of its parents’.',
        edit: 'Edit project',
        created: 'Project created.',
        saved: 'Project saved.',
        deleteConfirm: 'Delete this project, its sub-projects and their conversations?',
        tree: 'Project tree',
    },
    apiKey: {
        title: 'NVIDIA API key',
        subtitle: 'Chat calls NVIDIA with your key. It is encrypted, never leaves the server, and is never logged.',
        label: 'API key',
        placeholder: 'nvapi-…',
        hint: 'Create one at build.nvidia.com, then paste it here.',
        save: 'Save key',
        remove: 'Remove key',
        saved: 'Key saved and verified.',
        removed: 'Key removed.',
        configured: 'Key saved, ending in {suffix}.',
        notConfigured: 'No key saved.',
        validatedAt: 'Verified {when}',
        notVerified: 'Not verified yet — the provider could not be reached. The key is saved and will be used as it is.',
        lastError: 'Last failure: {message}',
        replaceHint: 'Saving a new key replaces this one.',
    },
    photos: {
        title: 'Photos',
        // The grid's accessible name. A list of a thousand tiles with no label is
        // an unnavigable wall to a screen reader.
        gallery: 'Gallery',
        emptyTitle: 'No photos yet',
        emptyBody: 'Add your first photos — they are encrypted before they leave the server.',
        add: 'Add photos',
        addMore: 'Add',
        // `alt` on a tile. The filename is what the person recognises; the date is
        // what tells two files of the same name apart.
        tileAlt: '{filename}, {date}',
        untitled: 'Untitled',
        loadingMore: 'Loading photos…',
        loadMore: 'Load more',
        // The service worker is what turns /media/<id> into a photo, so without it
        // the grid is a wall of broken images rather than a slow one.
        workerUnavailable: 'This browser cannot decrypt photos here, so they will not appear.',
        accepts: 'JPEG, PNG, WebP, AVIF, GIF and HEIC images, MP4, MOV and WebM video.',
        videoLabel: 'Video',
        // A duration on a video tile, already formatted.
        duration: '{duration}',
        noAccessTitle: 'No access to photos',
        // The full-screen view.
        //
        // The dialog's own accessible name. Its title is the filename, which is
        // what somebody navigating by landmark needs to hear, but "vacances.jpg"
        // on its own does not say what kind of thing has just opened.
        viewer: 'Photo',
        opening: 'Opening…',
        // Every link in the chain - a worker that never claimed the page, a key
        // that did not arrive, an expired signature - fails as a picture that does
        // not appear, so the message says no more than is known.
        openFailed: 'This photo could not be opened.',
        previous: 'Previous photo',
        next: 'Next photo',
        // Which photo of how many are loaded. Not of how many exist: the library is
        // paged, and a total nobody has counted would be a number that grows while
        // somebody reads it.
        position: '{index} of {total}',
        dimensions: '{width} × {height}',
        // For a photo whose file carried no capture time, which is common for
        // screenshots and anything through a messaging app.
        addedOn: 'Added {date}',
        // Said after a delete, with the way back beside it. The window is named
        // because "deleted" and "deleted for 30 days" are different promises.
        deleted: '{filename} is in the trash, and will be kept for {days} days.',
        undo: 'Undo',
        restored: '{filename} is back in your library.',
        // The trash.
        trash: 'Trash',
        trashTitle: 'Trash',
        // Said once at the top, so the rule is stated where somebody is deciding
        // what to do rather than only on the button that put things here.
        trashIntro: 'Photos stay here for {days} days, then they are deleted for good.',
        trashEmptyTitle: 'The trash is empty',
        trashEmptyBody: 'Photos you delete appear here, and you can put them back.',
        backToGallery: 'Back to photos',
        restore: 'Put back',
        // How long this item has left. Plural in the catalogue rather than computed,
        // because "1 days" is the sort of thing nobody notices until it ships.
        daysLeft: '{count} day left | {count} days left',
        // Its window has closed and the next sweep will take it. "0 days left"
        // reads as "already gone", which it is not - it is still restorable right
        // up until the sweep runs.
        dueNow: 'Due to be deleted',
        // The purge. Worded as the thing it does, not as "delete" again - the photo
        // has already been deleted once, and the difference is the whole point.
        deleteForGood: 'Delete for good',
        deleteForGoodTitle: 'Delete {filename} for good?',
        // States the irreversibility plainly, because it is genuinely absolute: the
        // key that opens the file lives in the row this destroys.
        deleteForGoodBody: 'This cannot be undone. The photo and everything made from it are deleted immediately.',
        purged: '{filename} is gone.',
        // Albums.
        albums: 'Albums',
        albumsTitle: 'Albums',
        albumsIntro: 'Arrange your photos by trip, by day, by whatever you like. An album can hold other albums.',
        albumsEmptyTitle: 'No albums yet',
        albumsEmptyBody: 'Create an album, then add photos to it from the gallery.',
        newAlbum: 'New album',
        albumTitle: 'Album name',
        createAlbum: 'Create',
        // Creating one inside another, from that album's own row.
        addSubAlbum: 'Add an album inside',
        renameAlbum: 'Rename',
        // How many photos are in this album itself - deliberately not counting the
        // ones in the albums beneath it, which would make a parent's number a
        // surprise.
        albumCount: 'no photos | 1 photo | {count} photos',
        openAlbum: 'Open',
        backToAlbums: 'Back to albums',
        // Deleting an album. Says what it does *not* do, because that is the part
        // somebody hesitating over the button is actually unsure about.
        deleteAlbumTitle: 'Delete the album {title}?',
        deleteAlbumBody: 'The photos are not deleted — they stay in your gallery. Any albums inside this one are deleted too.',
        albumDeleted: 'The album {title} was deleted.',
        albumEmptyTitle: 'This album is empty',
        albumEmptyBody: 'Open a photo in the gallery and add it to this album.',
        // Filing a photo, from the full-screen view.
        addToAlbum: 'Add to an album',
        addToAlbumTitle: 'Add to an album',
        addedToAlbum: 'Added to {title}.',
        alreadyInAlbum: 'Already in {title}.',
        removeFromAlbum: 'Remove from this album',
        removedFromAlbum: 'Removed from the album.',
        noAlbumsYet: 'Create an album first.',
        // Album suggestions. The server sends facts only - a place, two dates, a
        // count - because the job that finds them runs overnight with nobody signed
        // in and therefore no language to write in. The sentence is assembled here.
        suggestionsTitle: 'Suggestions',
        suggestionsIntro: 'Outings found in your photos, from when and where they were taken. Nothing is created until you accept.',
        // The proposed title, used as the album's name unless somebody edits it.
        // With no place the dates stand alone: "12–15 August 2026".
        suggestionTitle: '{place}, {dates}',
        suggestionCreate: 'Create the album',
        suggestionDismiss: 'No thanks',
        // Says what "no thanks" actually commits to, because it is permanent.
        suggestionDismissHint: 'These photos will not be suggested again.',
        suggestionPreview: 'See the photos',
        suggestionCreated: 'The album {title} was created.',
        suggestionDismissed: 'Suggestion dismissed.',
        suggestionMissing: 'That suggestion is gone.',
        // The place data (GeoNames, CC BY 4.0) is licensed on condition of
        // attribution wherever it is used.
        suggestionPlaces: 'Places: GeoNames',
        // Sharing groups. What is *not* here is part of the design: nobody sees the
        // invitations they have sent, because one disappearing would reveal that it
        // was declined.
        groups: 'Groups',
        groupsTitle: 'Sharing groups',
        groupsIntro: 'Share an album with the people you choose. Everything stays private until you share it.',
        groupsEmptyTitle: 'No groups yet',
        groupsEmptyBody: 'Create a group, invite somebody by their email address, then share an album with them.',
        newGroup: 'New group',
        groupName: 'Group name',
        createGroup: 'Create',
        openGroup: 'View',
        groupOwner: 'owner',
        memberCount: 'no members | 1 member | {count} members',
        // Inviting. The hint says "exact" because there is no search: you have to
        // know the address already.
        inviteLabel: 'Email address',
        inviteHint: 'Their exact address. There is no way to search for somebody.',
        sendInvitation: 'Invite',
        // The same sentence whatever happened: the server answers identically for an
        // address it knows, one it does not, and somebody already in the group.
        // Saying anything more would be inventing information this client lacks.
        invitationSent: 'If an account exists at that address, the invitation has been sent.',
        invitationsTitle: 'Invitations',
        invitedBy: 'Invitation from {email}',
        acceptInvitation: 'Join',
        declineInvitation: 'Decline',
        invitationAccepted: 'You joined {name}.',
        // Deliberately does not say the sender was told, because they are not.
        invitationDeclined: 'Invitation declined.',
        removeMember: 'Remove',
        memberRemoved: '{email} was removed from the group.',
        leaveGroup: 'Leave',
        groupLeft: 'You left {name}.',
        deleteGroupTitle: 'Delete the group {name}?',
        deleteGroupBody: 'No photo and no album is deleted: albums shared with this group become private again.',
        groupDeleted: 'The group {name} was deleted.',
        // Sharing an album.
        shareWith: 'Shared with',
        notShared: 'Private',
        albumShared: '{title} is shared with {group}.',
        albumUnshared: '{title} is no longer shared.',
        sharedBy: 'shared by {email}',
        // The notification centre. Every line here is assembled from facts on the
        // row - a group name, an album title, an address - because the person who
        // caused the event and the person reading about it may not share a
        // language, so the server stores no sentence at all.
        notifications: 'Notifications',
        notificationsUnread: 'Notifications, {count} unread',
        notificationsEmptyTitle: 'Nothing new',
        notificationsEmptyBody: 'Invitations and albums shared with you appear here.',
        notificationsMore: 'Show more',
        // Anything above this is shown as "99+", which is a badge and not a count.
        notificationsBadgeMax: '99+',
        notifiedInvitation: '{email} invited you to {group}.',
        notifiedMemberJoined: '{email} joined {group}.',
        notifiedAlbumShared: '{email} shared {album} with {group}.',
        // What is left on a card whose buttons have been spent. The invitation
        // itself is unchanged; only the offer is over.
        // Near-duplicates. Nothing here is decided by the sweep: it proposes, a
        // person disposes, and the three answers are the only ways out.
        duplicates: 'Duplicates',
        duplicatesTitle: 'Duplicates',
        duplicatesIntro: 'These photos look very much alike. Keep the one you prefer, or keep them all.',
        duplicatesEmptyTitle: 'No duplicates',
        duplicatesEmptyBody: 'Photos that look alike are grouped here, every night.',
        duplicateCount: 'no copies | 1 copy | {count} copies',
        duplicateKeeping: 'keeping',
        duplicateChoose: 'Move {filename} to the trash',
        // The count is in the label: this is the one action on the screen that
        // destroys anything, and knowing how many before pressing is the minimum.
        duplicateTrash: 'Nothing to delete | Move 1 photo to the trash | Move {count} photos to the trash',
        duplicateKeep: 'Keep them all',
        duplicateRemind: 'Later',
        duplicateKeepHint: 'Keep them all: these photos will never be proposed together again.',
        duplicateKept: 'All {count} photos kept.',
        duplicateReminded: 'We will ask again in a fortnight.',
        // Deliberately "to the trash" rather than "deleted": they are recoverable
        // for thirty days, like every other delete here.
        duplicateTrashed: 'Nothing moved. | 1 photo moved to the trash. | {count} photos moved to the trash.',
        notifiedAccepted: 'Joined',
        notifiedDeclined: 'Declined',
        notifiedExpired: 'No longer available',
    },
    upload: {
        title: 'Upload',
        // Progress across the whole selection rather than one file: somebody who
        // dropped forty photos wants to know when they can close the tab.
        progress: '{done} of {total}',
        hashing: 'Reading the file…',
        checking: 'Checking…',
        sending: 'Sending…',
        // A rate-limit wait, which is not a failure. The server asked for a pause;
        // the queue keeps its place and says how long, because "waiting" with no
        // number is indistinguishable from stuck.
        waiting: 'Waiting for the server…',
        waitingFor: 'Server busy — resuming in {seconds}s',
        finishing: 'Finishing…',
        thumbnail: 'Thumbnail…',
        done: 'Uploaded',
        duplicate: 'Already in the library',
        // Distinct from the line above, because the file *is* held and the gallery
        // does not show it. "Already in the library" about a photo somebody cannot
        // find reads as the upload having silently done nothing.
        trashed: 'Already here, in the trash',
        failed: 'Failed',
        retry: 'Try again',
        cancel: 'Cancel',
        dismiss: 'Dismiss',
        // Said once, when the whole batch has finished.
        allDone: 'Added {count} photo(s).',
        someFailed: '{count} upload(s) failed.',
        tooLarge: 'This file is larger than the {limit} maximum.',
        unsupported: 'This format is not accepted.',
    },
    status: {
        pending_verification: 'Awaiting verification',
        active: 'Active',
        locked: 'Locked',
        deleting: 'Being deleted',
    },
    mobile: {
        unreachableTitle: 'Server unreachable',
        unreachableBody: 'You are still signed in on this phone. Check your connection, then try again.',
        inBrowser: 'Opens in your browser.',
        backupTitle: 'Automatic backup',
        backupIntro: 'Photos in the folders you choose go up on their own: every hour, when the battery is not low, over the network you allow.',
        backupIntroWeb: 'Automatic backup is set up from the Android app. These are the phones backing up to this account.',
        thisPhone: 'This phone',
        accessNone: 'Allow access to your photos to choose the folders to back up.',
        accessPartial: 'Only some photos are allowed. An automatic backup needs all of them, or new photos would never be part of it.',
        allowAccess: 'Allow access',
        openSettings: 'Open settings',
        noLocation: 'Without access to their location, photos go up without their place, and no album can be suggested for them.',
        folders: 'Folders to back up',
        folderItems: 'empty | 1 item | {count} items',
        noFolders: 'No folder on this phone holds photos.',
        folderLimit: 'A phone can back up at most {max} folders. Untick one to choose another.',
        unreadable: 'Every photo could be read. | This photo could not be read after five tries, so it is not backed up: | These {count} photos could not be read after five tries, so they are not backed up:',
        unreadableMore: 'nothing more | and 1 more | and {count} more',
        networkFor: 'Network for {folder}',
        networkUnmetered: 'Wi-Fi only',
        networkAny: 'Wi-Fi and mobile data',
        backUpNow: 'Back up now',
        lastBackup: 'Last backup {when}',
        neverBackedUp: 'No backup yet.',
        uploadedCount: 'no photos uploaded | 1 photo uploaded | {count} photos uploaded',
        pendingCount: 'nothing waiting | 1 waiting | {count} waiting',
        runState: {
            running: 'Backing up…',
            'signed-out': 'Backup paused: sign in again.',
            permission: 'Backup paused: access to your photos was withdrawn.',
            quota: 'Backup paused: your account is out of space.',
            removed: 'This phone was removed from the account. Choose folders to resume.',
            failed: 'The last backup did not finish; it will be retried.',
            'update-required': 'Backup was paused until the app was updated. It resumes on its next run.',
        },
        phones: 'Phones on this account',
        noPhones: 'No phone backs up to this account.',
        phoneFolders: 'no folders | 1 folder | {count} folders',
        removePhone: 'Remove',
        removePhoneTitle: 'Remove {name}?',
        removePhoneBody: 'It stops backing up the next time it tries. Photos already uploaded stay in your library.',
    },
    apps: {
        identity: 'Account',
        chat: 'Chat',
        photos: 'Photos',
    },
    welcome: {
        title: 'Welcome',
        subtitle: 'A private space for the family: one account to chat with an AI and to keep your photos.',
        appsHeading: 'The applications',
        chat: 'Chat with AI models, over your own documents too, and pick a conversation up where you left it.',
        photos: 'Your photos and videos, encrypted, filed in albums and shared with the people you choose.',
        identity: 'One account for everything, protected by two-factor authentication.',
    },
    metrics: {
        storage_bytes: 'Storage',
        media_items: 'Photos and videos',
        albums: 'Albums',
        ai_messages_per_month: 'AI messages this month',
        ai_projects: 'AI projects',
        groups: 'Groups',
    },
    errors: {
        'validation.failed': 'Some details are invalid. Check the form.',
        'auth.unauthenticated': 'You need to be signed in to continue.',
        'auth.invalid_credentials': 'Incorrect email address or password.',
        'auth.email_not_verified': 'Verify your email address before continuing.',
        'auth.totp_required': 'Enter your authentication code.',
        'auth.totp_invalid': 'That code is incorrect or has expired.',
        'auth.totp_enrolment_required': 'Set up two-factor authentication to activate your account.',
        'auth.account_locked': 'This account is temporarily locked. Try again later.',
        'auth.account_deletion_scheduled': 'This account is being deleted. To keep it, follow the link in the email we sent you.',
        'auth.session_expired': 'Your session has expired. Please sign in again.',
        'auth.forbidden': 'You do not have access to this resource.',
        'auth.reauthentication_required': 'Confirm your identity to perform this action.',
        'resource.not_found': 'That item could not be found.',
        'resource.conflict': 'That action conflicts with the current state.',
        'resource.gone': 'That item is no longer available.',
        'upload.duplicate': 'This file has already been uploaded.',
        'upload.too_large': 'This file is too large.',
        'upload.unsupported_type': 'That file type is not supported.',
        'upload.no_text': 'No text could be read from this file. A scanned PDF has to be run through OCR first.',
        'upload.incomplete': 'This upload is not finished: some parts of the file are still missing.',
        'quota.exceeded': 'You have reached the limit of your plan.',
        'rate_limit.exceeded': 'Too many attempts. Wait a moment.',
        'credentials.missing': 'Add your API key in your settings to start chatting.',
        'stream.capacity_exceeded': 'Too many replies are being written at once. Try again in a moment.',
        'upstream.unavailable': 'The remote service is unavailable. Try again later.',
        'upstream.invalid_credentials': 'The provider rejected the API key. Check it in your settings.',
        'upstream.content_filtered': 'The model declined to answer this request.',
        // Names the cause, because it is unusual enough that "something went wrong"
        // would send somebody looking in the wrong place: the model was working the
        // whole time, it just never stopped thinking.
        'upstream.no_answer': 'The model spent its whole reply thinking and never answered. Try a shorter question, or another model.',
        // Sends the reader to their provider account rather than to another model,
        // because nothing is wrong with the model or with the key itself - the
        // account simply is not entitled to this one.
        'upstream.model_unavailable': 'Your API key does not have access to this model. Pick another one, or enable it on your NVIDIA account.',
        // Not an internal error, and the wording is the whole point: their
        // photographs are untouched, a third party is having a bad minute, and
        // trying again shortly is the right thing to do. The gallery puts a button
        // beside this rather than leaving somebody to guess when.
        'storage.unavailable': 'Photos are briefly unavailable. Nothing has been lost — try again in a moment.',
        'app.update_required': 'This version of the app is no longer supported. Update it from the Play Store.',
        'server.internal': 'Something unexpected went wrong.',
        network: 'Could not connect. Check your internet connection.',
        unknown: 'Something unexpected went wrong.',
    },
};
//# sourceMappingURL=en.js.map